import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "feedback"]
  static values = {
    url: String,
    excludeId: { type: String, default: "" }
  }

  connect() {
    this.timeout = null
    this.abortController = null
    this.originalEmail = this.inputTarget.value.trim().toLowerCase()
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.abortController?.abort()
  }

  check() {
    clearTimeout(this.timeout)
    this.abortController?.abort()

    const email = this.inputTarget.value.trim()

    if (!email || !this.isValidEmail(email)) {
      this.clearFeedback()
      this.updateInputStyle(null)
      return
    }

    if (email.toLowerCase() === this.originalEmail) {
      this.clearFeedback()
      this.updateInputStyle(null)
      return
    }

    this.showChecking()
    this.timeout = setTimeout(() => this.performCheck(email), 400)
  }

  async performCheck(email) {
    this.abortController = new AbortController()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("email", email)
      if (this.excludeIdValue) {
        url.searchParams.set("exclude_id", this.excludeIdValue)
      }

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) return

      const data = await response.json()

      if (data.available) {
        this.showAvailable()
        this.updateInputStyle(true)
      } else {
        this.showTaken()
        this.updateInputStyle(false)
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        this.clearFeedback()
        this.updateInputStyle(null)
      }
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  showChecking() {
    this.feedbackTarget.innerHTML = `
      <span class="text-xs text-gray-500 flex items-center gap-1">
        <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Checking...
      </span>`
    this.feedbackTarget.classList.remove("hidden")
  }

  showAvailable() {
    this.feedbackTarget.innerHTML = `
      <span class="text-xs text-green-600 flex items-center gap-1">
        <svg class="h-3 w-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        Email is available
      </span>`
    this.feedbackTarget.classList.remove("hidden")
  }

  showTaken() {
    this.feedbackTarget.innerHTML = `
      <span class="text-xs text-red-600 flex items-center gap-1">
        <svg class="h-3 w-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        Email is already taken
      </span>`
    this.feedbackTarget.classList.remove("hidden")
  }

  clearFeedback() {
    this.feedbackTarget.innerHTML = ""
    this.feedbackTarget.classList.add("hidden")
  }

  updateInputStyle(available) {
    const input = this.inputTarget
    input.classList.remove("border-green-500", "border-red-500", "border-gray-300")

    if (available === true) {
      input.classList.add("border-green-500")
    } else if (available === false) {
      input.classList.add("border-red-500")
    } else {
      input.classList.add("border-gray-300")
    }
  }
}
